import React, { useState, useEffect } from 'react';
import { reportService, formatBarangayName } from '../../services/reportService';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Stack,
  LinearProgress,
  Divider,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  WaterDrop as WaterIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  AccessTime as ClockIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const ReportsPage = () => {
  const [activeReport, setActiveReport] = useState('water'); // water, revenue, collections, aging, customers
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filters
  const [selectedBarangays, setSelectedBarangays] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [barangays, setBarangays] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  
  // Report data
  const [waterReport, setWaterReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  const [collectionReport, setCollectionReport] = useState(null);
  const [topConsumers, setTopConsumers] = useState([]);
  const [agingReport, setAgingReport] = useState(null);
  const [customerReport, setCustomerReport] = useState(null);

  useEffect(() => {
    loadFilterOptions();
  }, []);
  
  const loadFilterOptions = async () => {
    try {
      const { data: barangayData } = await reportService.getBarangays();
      const { data: typeData } = await reportService.getCustomerTypes();
      const { data: monthsData } = await reportService.getBilledMonths();
      
      if (barangayData) setBarangays(barangayData);
      if (typeData) setCustomerTypes(typeData);
      if (monthsData) {
        setAvailableMonths(monthsData);
        // Default to the most recent month if available
        if (monthsData.length > 0) {
          setSelectedMonths([monthsData[0]]);
        }
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };

  useEffect(() => {
    loadReports();
  }, [selectedMonths, selectedBarangays, selectedTypes, selectedStatus]);

  const loadReports = async () => {
    if (selectedMonths.length === 0) {
      setWaterReport(null);
      setRevenueReport(null);
      setCollectionReport(null);
      setTopConsumers([]);
      setAgingReport(null);
      setCustomerReport(null);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const filters = {
        barangay: selectedBarangays,
        type: selectedTypes,
        status: selectedStatus
      };

      const [waterResult, revenueResult, collectionResult, topResult, agingResult, customerResult] = await Promise.all([
        reportService.getWaterUsageReport(selectedMonths, filters),
        reportService.getRevenueReport(selectedMonths, filters),
        reportService.getCollectionReport(selectedMonths, filters),
        reportService.getTopConsumers(selectedMonths, 10, filters),
        reportService.getAgingReport(filters, selectedMonths),
        reportService.getCustomerAnalysis(filters)
      ]);

      if (waterResult.success) setWaterReport(waterResult.data);
      if (revenueResult.success) setRevenueReport(revenueResult.data);
      if (collectionResult.success) setCollectionReport(collectionResult.data);
      if (topResult.success) setTopConsumers(topResult.data);
      if (agingResult.success) setAgingReport(agingResult.data);
      if (customerResult.success) setCustomerReport(customerResult.data);

      if (!waterResult.success || !revenueResult.success || !collectionResult.success) {
        setError('Some reports failed to load');
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setError('Failed to load reports');
    }

    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 });
  };

  const formatMonth = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-PH', { year: 'numeric', month: 'long' });
  };

  const exportToCSV = (data, filename) => {
    const csv = data.map(row => Object.values(row).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSelectAllMonths = () => {
    setSelectedMonths(availableMonths);
  };

  const handleClearAllMonths = () => {
    setSelectedMonths([]);
  };

  const isExportEnabled = () => {
    if (activeReport === 'water' && waterReport) return true;
    if (activeReport === 'revenue' && revenueReport) return true;
    if (activeReport === 'collections' && collectionReport) return true;
    if (activeReport === 'aging' && agingReport) return true;
    if (activeReport === 'customers' && customerReport) return true;
    return false;
  };

  const styleHeaderRow = (sheet, rowNum) => {
    const row = sheet.getRow(rowNum);
    row.font = { name: 'Calibri', size: 11, bold: true };
    row.eachCell(cell => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE5E7EB' }
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
  };

  const handleExportExcel = async () => {
    if (loading) return;

    try {
      const workbook = new ExcelJS.Workbook();
      const monthNamesStr = selectedMonths.length > 0 
        ? selectedMonths.map(m => formatMonth(m)).join(', ') 
        : 'All Months';
      const filename = `Water_Billing_Report_${activeReport}_${new Date().toISOString().split('T')[0]}`;

      if (activeReport === 'water' && waterReport) {
        // Sheet 1: Summary
        const summarySheet = workbook.addWorksheet('Summary');
        summarySheet.addRow(['Water Usage Summary Report']);
        summarySheet.addRow([`Selected Months: ${monthNamesStr}`]);
        summarySheet.addRow([`Generated on: ${new Date().toLocaleString('en-PH')}`]);
        summarySheet.addRow([]);
        summarySheet.addRow(['Metric', 'Value']);
        summarySheet.addRow(['Total Consumption', `${formatNumber(waterReport.totalConsumption)} cu.m`]);
        summarySheet.addRow(['Average Consumption', `${formatNumber(waterReport.averageConsumption)} cu.m`]);
        summarySheet.addRow(['Total Bills Issued', waterReport.billCount]);
        
        styleHeaderRow(summarySheet, 1);
        styleHeaderRow(summarySheet, 5);

        // Sheet 2: Consumption by Barangay
        const brgySheet = workbook.addWorksheet('By Barangay');
        brgySheet.addRow(['Water Consumption by Barangay']);
        brgySheet.addRow([`Selected Months: ${monthNamesStr}`]);
        brgySheet.addRow([]);

        const headers = ['Barangay', 'No. of Customers'];
        selectedMonths.forEach(m => {
          headers.push(formatMonth(m));
        });
        if (selectedMonths.length > 1) {
          headers.push('Total Consumption (cu.m)');
        }
        brgySheet.addRow(headers);

        waterReport.byBarangay.forEach(item => {
          const rowData = [formatBarangayName(item.barangay), item.customerCount || 0];
          selectedMonths.forEach(month => {
            rowData.push(item.monthlyConsumption?.[month] || 0);
          });
          if (selectedMonths.length > 1) {
            rowData.push(item.consumption);
          }
          brgySheet.addRow(rowData);
        });

        // Add Total row
        const totals = {
          monthly: {},
          consumption: 0,
          count: 0,
          customerCount: 0
        };
        selectedMonths.forEach(m => { totals.monthly[m] = 0; });
        waterReport.byBarangay.forEach(item => {
          selectedMonths.forEach(month => {
            totals.monthly[month] += (item.monthlyConsumption?.[month] || 0);
          });
          totals.consumption += item.consumption;
          totals.count += item.count;
          totals.customerCount += (item.customerCount || 0);
        });

        const totalRow = ['Total', totals.customerCount];
        selectedMonths.forEach(month => {
          totalRow.push(totals.monthly[month]);
        });
        if (selectedMonths.length > 1) {
          totalRow.push(totals.consumption);
        }
        const addedBrgyTotal = brgySheet.addRow(totalRow);
        addedBrgyTotal.font = { name: 'Calibri', size: 11, bold: true };
        addedBrgyTotal.eachCell(cell => {
          cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'double' }
          };
        });

        styleHeaderRow(brgySheet, 4);

      } else if (activeReport === 'revenue' && revenueReport) {
        // Sheet 1: Summary
        const summarySheet = workbook.addWorksheet('Summary');
        summarySheet.addRow(['Revenue Summary Report']);
        summarySheet.addRow([`Selected Months: ${monthNamesStr}`]);
        summarySheet.addRow([`Generated on: ${new Date().toLocaleString('en-PH')}`]);
        summarySheet.addRow([]);
        summarySheet.addRow(['Metric', 'Amount']);
        summarySheet.addRow(['Total Billed', formatCurrency(revenueReport.totalBilled)]);
        summarySheet.addRow(['Total Paid', formatCurrency(revenueReport.totalPaid)]);
        summarySheet.addRow(['Total Unpaid', formatCurrency(revenueReport.totalUnpaid)]);
        summarySheet.addRow(['Surcharges Billed', formatCurrency(revenueReport.totalSurcharge)]);
        summarySheet.addRow(['Discounts Applied', formatCurrency(revenueReport.totalDiscount)]);
        summarySheet.addRow(['Paid Bills Count', revenueReport.paidCount]);
        summarySheet.addRow(['Unpaid Bills Count', revenueReport.unpaidCount]);
        summarySheet.addRow(['Collection Rate', `${revenueReport.collectionRate.toFixed(1)}%`]);

        styleHeaderRow(summarySheet, 1);
        styleHeaderRow(summarySheet, 5);

        // Sheet 2: Monthly Revenue
        const monthSheet = workbook.addWorksheet('Monthly Revenue');
        monthSheet.addRow(['Monthly Revenue Breakdown']);
        monthSheet.addRow(['Month', 'Billed', 'Paid', 'Unpaid', 'Collection Rate']);
        revenueReport.byMonth.forEach(item => {
          monthSheet.addRow([
            formatMonth(item.month),
            item.billed,
            item.paid,
            item.unpaid,
            item.billed > 0 ? `${((item.paid / item.billed) * 100).toFixed(1)}%` : '0%'
          ]);
        });
        styleHeaderRow(monthSheet, 2);

        // Sheet 3: Barangay Revenue
        const brgySheet = workbook.addWorksheet('Barangay Revenue');
        brgySheet.addRow(['Revenue by Barangay']);
        brgySheet.addRow(['Barangay', 'Billed', 'Paid', 'Unpaid', 'Bill Count']);
        revenueReport.byBarangay.forEach(item => {
          brgySheet.addRow([
            item.barangay,
            item.billed,
            item.paid,
            item.unpaid,
            item.billCount
          ]);
        });
        styleHeaderRow(brgySheet, 2);

      } else if (activeReport === 'collections' && collectionReport) {
        // Sheet 1: Summary
        const summarySheet = workbook.addWorksheet('Summary');
        summarySheet.addRow(['Collection Summary Report']);
        summarySheet.addRow([`Selected Months: ${monthNamesStr}`]);
        summarySheet.addRow([`Generated on: ${new Date().toLocaleString('en-PH')}`]);
        summarySheet.addRow([]);
        summarySheet.addRow(['Metric', 'Value']);
        summarySheet.addRow(['Total Collected', formatCurrency(collectionReport.totalCollected)]);
        summarySheet.addRow(['Total Payments Received', collectionReport.collectionCount]);
        summarySheet.addRow(['Average per Collection', collectionReport.collectionCount > 0 ? formatCurrency(collectionReport.totalCollected / collectionReport.collectionCount) : '₱0.00']);

        styleHeaderRow(summarySheet, 1);
        styleHeaderRow(summarySheet, 5);

        // Sheet 2: Daily Collections
        const dailySheet = workbook.addWorksheet('Daily Collections');
        dailySheet.addRow(['Daily Collections Breakdown']);
        dailySheet.addRow(['Date', 'Amount Collected', 'Payment Count', 'Average Payment']);
        collectionReport.byDate.forEach(item => {
          dailySheet.addRow([
            new Date(item.date).toLocaleDateString('en-PH'),
            item.amount,
            item.count,
            item.count > 0 ? item.amount / item.count : 0
          ]);
        });
        styleHeaderRow(dailySheet, 2);

        // Sheet 3: By Collector
        const collSheet = workbook.addWorksheet('By Collector');
        collSheet.addRow(['Collections by Collector']);
        collSheet.addRow(['Collector', 'Total Collected', 'Receipts Count', 'Average Collection']);
        collectionReport.byCollector.forEach(item => {
          collSheet.addRow([
            item.collector,
            item.amount,
            item.count,
            item.count > 0 ? item.amount / item.count : 0
          ]);
        });
        styleHeaderRow(collSheet, 2);

      } else if (activeReport === 'aging' && agingReport) {
        // Sheet 1: Aging Summary
        const agingSheet = workbook.addWorksheet('Aging Summary');
        agingSheet.addRow(['Accounts Receivable Aging Report']);
        agingSheet.addRow([`Generated on: ${new Date().toLocaleString('en-PH')}`]);
        agingSheet.addRow([]);
        agingSheet.addRow(['Age Bracket', 'Bills Count', 'Unpaid Amount']);

        Object.entries(agingReport.summary).forEach(([key, value]) => {
          let title = key;
          if (key === 'current') title = 'Current (0-30 days)';
          if (key === 'days_31_60') title = '31-60 days';
          if (key === 'days_61_90') title = '61-90 days';
          if (key === 'days_91_120') title = '91-120 days';
          if (key === 'over_120') title = 'Over 120 days';

          agingSheet.addRow([title, value.count, value.amount]);
        });
        agingSheet.addRow(['Total Outstanding', agingReport.totalCount, agingReport.totalAmount]);

        styleHeaderRow(agingSheet, 1);
        styleHeaderRow(agingSheet, 4);

      } else if (activeReport === 'customers' && customerReport) {
        // Sheet 1: Status Breakdown
        const statusSheet = workbook.addWorksheet('By Status');
        statusSheet.addRow(['Customer Status Analysis']);
        statusSheet.addRow([`Generated on: ${new Date().toLocaleString('en-PH')}`]);
        statusSheet.addRow([]);
        statusSheet.addRow(['Status', 'Customer Count', 'Percentage']);
        customerReport.byStatus.forEach(item => {
          statusSheet.addRow([
            item.status,
            item.count,
            customerReport.total > 0 ? item.count / customerReport.total : 0
          ]);
        });
        statusSheet.addRow(['Total Customers', customerReport.total, 1]);
        styleHeaderRow(statusSheet, 1);
        styleHeaderRow(statusSheet, 4);

        // Sheet 2: Type Breakdown
        const typeSheet = workbook.addWorksheet('By Type');
        typeSheet.addRow(['Customer Type Analysis']);
        typeSheet.addRow(['Type', 'Customer Count', 'Percentage']);
        customerReport.byType.forEach(item => {
          typeSheet.addRow([
            item.type,
            item.count,
            customerReport.total > 0 ? item.count / customerReport.total : 0
          ]);
        });
        styleHeaderRow(typeSheet, 1);
        styleHeaderRow(typeSheet, 2);

        // Sheet 3: Barangay Breakdown
        const brgySheet = workbook.addWorksheet('By Barangay');
        brgySheet.addRow(['Customer Barangay Analysis']);
        brgySheet.addRow(['Barangay', 'Customer Count', 'Percentage']);
        customerReport.byBarangay.forEach(item => {
          brgySheet.addRow([
            item.barangay,
            item.count,
            customerReport.total > 0 ? item.count / customerReport.total : 0
          ]);
        });
        styleHeaderRow(brgySheet, 1);
        styleHeaderRow(brgySheet, 2);
      }

      // Auto-fit column widths and format borders/alignment
      workbook.worksheets.forEach(sheet => {
        sheet.columns.forEach(column => {
          let maxLen = 0;
          column.eachCell({ includeEmpty: true }, cell => {
            const valueStr = cell.value ? String(cell.value) : '';
            if (valueStr.length > maxLen) maxLen = valueStr.length;
          });
          column.width = Math.max(maxLen + 4, 12);
        });
      });

      // Write to buffer and save
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${filename}.xlsx`);

    } catch (err) {
      console.error('Error exporting Excel:', err);
      setError('Failed to export Excel report');
    }
  };

  const handleExportWaterUsageExcel = async () => {
    if (!waterReport || !waterReport.byBarangay) return;

    try {
      const workbook = new ExcelJS.Workbook();
      const monthNamesStr = selectedMonths.length > 0 
        ? selectedMonths.map(m => formatMonth(m)).join(', ') 
        : 'All Months';
      const filename = `Water_Usage_by_Barangay_${new Date().toISOString().split('T')[0]}`;

      const sheet = workbook.addWorksheet('Barangay Consumption');

      // Title
      sheet.addRow(['Water Consumption by Barangay Report']);
      sheet.addRow([`Selected Months: ${monthNamesStr}`]);
      sheet.addRow([`Generated on: ${new Date().toLocaleString('en-PH')}`]);
      sheet.addRow([]);

      // Headers
      const headers = ['Barangay', 'No. of Customers'];
      selectedMonths.forEach(m => {
        headers.push(formatMonth(m));
      });
      if (selectedMonths.length > 1) {
        headers.push('Total Consumption (cu.m)');
      }
      sheet.addRow(headers);

      // Data rows
      waterReport.byBarangay.forEach(item => {
        const rowData = [formatBarangayName(item.barangay), item.customerCount || 0];
        selectedMonths.forEach(month => {
          rowData.push(item.monthlyConsumption?.[month] || 0);
        });
        if (selectedMonths.length > 1) {
          rowData.push(item.consumption);
        }
        sheet.addRow(rowData);
      });

      // Add Total row
      const totals = {
        monthly: {},
        consumption: 0,
        count: 0,
        customerCount: 0
      };
      selectedMonths.forEach(m => { totals.monthly[m] = 0; });
      waterReport.byBarangay.forEach(item => {
        selectedMonths.forEach(month => {
          totals.monthly[month] += (item.monthlyConsumption?.[month] || 0);
        });
        totals.consumption += item.consumption;
        totals.count += item.count;
        totals.customerCount += (item.customerCount || 0);
      });

      const totalRow = ['Total', totals.customerCount];
      selectedMonths.forEach(month => {
        totalRow.push(totals.monthly[month]);
      });
      if (selectedMonths.length > 1) {
        totalRow.push(totals.consumption);
      }
      const addedBrgyTotal = sheet.addRow(totalRow);
      addedBrgyTotal.font = { name: 'Calibri', size: 11, bold: true };
      addedBrgyTotal.eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'double' }
        };
      });

      // Style headers
      styleHeaderRow(sheet, 1);
      styleHeaderRow(sheet, 5);

      // Autofit width
      sheet.columns.forEach(column => {
        let maxLen = 0;
        column.eachCell({ includeEmpty: true }, cell => {
          const valueStr = cell.value ? String(cell.value) : '';
          if (valueStr.length > maxLen) maxLen = valueStr.length;
        });
        column.width = Math.max(maxLen + 4, 12);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${filename}.xlsx`);

    } catch (err) {
      console.error('Error exporting water usage excel:', err);
      setError('Failed to export water usage excel');
    }
  };

  const handlePrint = () => {
    let title = '';
    let contentHtml = '';
    const generatedAt = new Date().toLocaleString('en-PH');
    
    const monthStr = selectedMonths.length > 0 ? selectedMonths.map(m => formatMonth(m)).join(', ') : 'All Months';
    const barangayStr = selectedBarangays.length > 0 ? selectedBarangays.join(', ') : 'All Barangays';
    const typeStr = selectedTypes.length > 0 ? selectedTypes.join(', ') : 'All Types';
    const statusStr = selectedStatus === 'all' ? 'All Statuses' : selectedStatus;

    if (activeReport === 'water' && waterReport) {
      title = 'Water Usage by Barangay Report';
      
      const monthHeaders = selectedMonths.map(m => `<th>${formatMonth(m)}</th>`).join('');
      const showTotalCol = selectedMonths.length > 1;
      const totalHeader = showTotalCol ? '<th>Total Consumption</th>' : '';

      const rows = waterReport.byBarangay.map(item => {
        const monthCols = selectedMonths.map(m => `<td>${formatNumber(item.monthlyConsumption?.[m] || 0)} cu.m</td>`).join('');
        const totalCol = showTotalCol ? `<td style="font-weight: bold;">${formatNumber(item.consumption)} cu.m</td>` : '';
        return `
          <tr>
            <td style="font-weight: bold;">${formatBarangayName(item.barangay)}</td>
            <td>${item.customerCount || 0}</td>
            ${monthCols}
            ${totalCol}
          </tr>
        `;
      }).join('');

      // Add Total row
      const totals = {
        monthly: {},
        consumption: 0,
        count: 0,
        customerCount: 0
      };
      selectedMonths.forEach(m => { totals.monthly[m] = 0; });
      waterReport.byBarangay.forEach(item => {
        selectedMonths.forEach(month => {
          totals.monthly[month] += (item.monthlyConsumption?.[month] || 0);
        });
        totals.consumption += item.consumption;
        totals.count += item.count;
        totals.customerCount += (item.customerCount || 0);
      });

      const totalMonthCols = selectedMonths.map(m => `<td style="font-weight: bold;">${formatNumber(totals.monthly[m] || 0)} cu.m</td>`).join('');
      const totalRowCol = showTotalCol ? `<td>${formatNumber(totals.consumption)} cu.m</td>` : '';
      const totalRowHtml = `
        <tr style="font-weight: bold; background-color: #f3f4f6;">
          <td>Total</td>
          <td>${totals.customerCount}</td>
          ${totalMonthCols}
          ${totalRowCol}
        </tr>
      `;

      contentHtml = `
        <table>
          <thead>
            <tr>
              <th>Barangay</th>
              <th>No. of Customers</th>
              ${monthHeaders}
              ${totalHeader}
            </tr>
          </thead>
          <tbody>
            ${rows}
            ${totalRowHtml}
          </tbody>
        </table>
      `;
    } else if (activeReport === 'revenue' && revenueReport) {
      title = 'Revenue Summary & Barangay Breakdown Report';
      
      const brgyRows = revenueReport.byBarangay.map(item => `
        <tr>
          <td>${item.barangay}</td>
          <td>${formatCurrency(item.billed)}</td>
          <td>${formatCurrency(item.paid)}</td>
          <td>${formatCurrency(item.unpaid)}</td>
          <td>${item.billCount}</td>
        </tr>
      `).join('');

      contentHtml = `
        <h3>Summary Metrics</h3>
        <ul>
          <li><strong>Total Billed:</strong> ${formatCurrency(revenueReport.totalBilled)}</li>
          <li><strong>Total Paid:</strong> ${formatCurrency(revenueReport.totalPaid)}</li>
          <li><strong>Total Unpaid:</strong> ${formatCurrency(revenueReport.totalUnpaid)}</li>
          <li><strong>Surcharges Billed:</strong> ${formatCurrency(revenueReport.totalSurcharge)}</li>
          <li><strong>Discounts Applied:</strong> ${formatCurrency(revenueReport.totalDiscount)}</li>
          <li><strong>Collection Rate:</strong> ${revenueReport.collectionRate.toFixed(1)}%</li>
        </ul>
        <h3>Barangay Breakdown</h3>
        <table>
          <thead>
            <tr>
              <th>Barangay</th>
              <th>Billed</th>
              <th>Paid</th>
              <th>Unpaid</th>
              <th>Bill Count</th>
            </tr>
          </thead>
          <tbody>
            ${brgyRows}
          </tbody>
        </table>
      `;
    } else if (activeReport === 'collections' && collectionReport) {
      title = 'Payment Collections Report';
      
      const dateRows = collectionReport.byDate.map(item => `
        <tr>
          <td>${new Date(item.date).toLocaleDateString('en-PH')}</td>
          <td>${formatCurrency(item.amount)}</td>
          <td>${item.count}</td>
        </tr>
      `).join('');

      const collectorRows = collectionReport.byCollector.map(item => `
        <tr>
          <td>${item.collector}</td>
          <td>${formatCurrency(item.amount)}</td>
          <td>${item.count}</td>
        </tr>
      `).join('');

      contentHtml = `
        <h3>Summary Metrics</h3>
        <ul>
          <li><strong>Total Collected:</strong> ${formatCurrency(collectionReport.totalCollected)}</li>
          <li><strong>Total Payments Received:</strong> ${collectionReport.collectionCount}</li>
        </ul>
        <h3>Collections by Date</h3>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount Collected</th>
              <th>Receipts Count</th>
            </tr>
          </thead>
          <tbody>
            ${dateRows}
          </tbody>
        </table>
        <h3>Collections by Collector</h3>
        <table>
          <thead>
            <tr>
              <th>Collector</th>
              <th>Total Collected</th>
              <th>Receipts Count</th>
            </tr>
          </thead>
          <tbody>
            ${collectorRows}
          </tbody>
        </table>
      `;
    } else if (activeReport === 'aging' && agingReport) {
      title = 'Accounts Receivable Aging Report';

      const summaryRows = Object.entries(agingReport.summary).map(([key, val]) => {
        let label = key;
        if (key === 'current') label = 'Current (0-30 days)';
        if (key === 'days_31_60') label = '31-60 days';
        if (key === 'days_61_90') label = '61-90 days';
        if (key === 'days_91_120') label = '91-120 days';
        if (key === 'over_120') label = 'Over 120 days';

        return `
          <tr>
            <td>${label}</td>
            <td>${val.count}</td>
            <td>${formatCurrency(val.amount)}</td>
          </tr>
        `;
      }).join('');

      contentHtml = `
        <h3>Aging Summary</h3>
        <table>
          <thead>
            <tr>
              <th>Age Bracket</th>
              <th>Bills Count</th>
              <th>Unpaid Amount</th>
            </tr>
          </thead>
          <tbody>
            ${summaryRows}
            <tr style="font-weight: bold; background-color: #f3f4f6;">
              <td>Total Outstanding</td>
              <td>${agingReport.totalCount}</td>
              <td>${formatCurrency(agingReport.totalAmount)}</td>
            </tr>
          </tbody>
        </table>
      `;
    } else if (activeReport === 'customers' && customerReport) {
      title = 'Customer Analysis Report';

      const statusRows = customerReport.byStatus.map(item => `
        <tr>
          <td>${item.status}</td>
          <td>${item.count}</td>
          <td>${((item.count / customerReport.total) * 100).toFixed(1)}%</td>
        </tr>
      `).join('');

      const typeRows = customerReport.byType.map(item => `
        <tr>
          <td>${item.type}</td>
          <td>${item.count}</td>
          <td>${((item.count / customerReport.total) * 100).toFixed(1)}%</td>
        </tr>
      `).join('');

      const brgyRows = customerReport.byBarangay.map(item => `
        <tr>
          <td>${item.barangay}</td>
          <td>${item.count}</td>
          <td>${((item.count / customerReport.total) * 100).toFixed(1)}%</td>
        </tr>
      `).join('');

      contentHtml = `
        <h3>Summary Metrics</h3>
        <ul>
          <li><strong>Total Customers:</strong> ${customerReport.total}</li>
          <li><strong>Active Customers:</strong> ${customerReport.active}</li>
          <li><strong>Inactive Customers:</strong> ${customerReport.inactive}</li>
        </ul>
        <h3>Breakdown by Status</h3>
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Customer Count</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            ${statusRows}
          </tbody>
        </table>
        <h3>Breakdown by Type</h3>
        <table>
          <thead>
            <tr>
              <th>Customer Type</th>
              <th>Customer Count</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            ${typeRows}
          </tbody>
        </table>
        <h3>Breakdown by Barangay</h3>
        <table>
          <thead>
            <tr>
              <th>Barangay</th>
              <th>Customer Count</th>
              <th>Percentage</th>
            </tr>
          </thead>
          <tbody>
            ${brgyRows}
          </tbody>
        </table>
      `;
    }

    if (!title || !contentHtml) return;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #333; }
            
            /* Letterhead style */
            .letterhead {
              text-align: center;
              margin-bottom: 5px;
              line-height: 1.5;
            }
            .letterhead .gov-text {
              font-size: 10px;
              letter-spacing: 2px;
              text-transform: uppercase;
              color: #4b5563;
              font-weight: 600;
            }
            .letterhead .municipality {
              font-size: 15px;
              font-weight: bold;
              color: #111827;
              margin-top: 2px;
              letter-spacing: 0.5px;
            }
            .letterhead .province {
              font-size: 12px;
              color: #4b5563;
              margin-top: 1px;
            }
            .letterhead .office {
              font-size: 13px;
              font-weight: bold;
              color: #1e3a8a;
              margin-top: 5px;
              letter-spacing: 0.5px;
            }
            .letterhead .system-title {
              font-size: 11px;
              font-weight: bold;
              color: #0d9488;
              margin-top: 2px;
              letter-spacing: 1.2px;
            }
            .divider-line {
              border-bottom: 2px solid #1e3a8a;
              border-top: 1px solid #4b5563;
              height: 2px;
              margin: 12px 0 20px 0;
            }

            .report-header-container {
              margin-bottom: 25px;
            }
            .report-title {
              text-align: center;
              font-size: 18px;
              font-weight: bold;
              color: #111827;
              margin: 15px 0 20px 0;
              text-transform: uppercase;
              letter-spacing: 0.8px;
            }

            /* Meta table style */
            .meta-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 25px;
              font-size: 11px;
              background-color: #f9fafb;
              border: 1px solid #e5e7eb;
              border-radius: 4px;
            }
            .meta-table td {
              border: none;
              padding: 6px 12px;
              vertical-align: top;
            }
            .meta-table td.label {
              font-weight: bold;
              color: #4b5563;
              width: 18%;
            }
            .meta-table td.value {
              color: #111827;
              width: 32%;
            }

            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 12px; }
            th, td { border: 1px solid #d1d5db; padding: 8px 10px; text-align: left; }
            th { background-color: #f3f4f6; font-weight: bold; }
            tr:hover { background-color: #f9fafb; }
            h3 { color: #1e3a8a; margin-top: 25px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; font-size: 14px; }
            ul { font-size: 12px; line-height: 1.6; }
            .no-print { margin-top: 30px; text-align: center; border-top: 1px solid #e5e7eb; padding-top: 20px; }
            .btn {
              padding: 8px 18px;
              font-size: 13px;
              border-radius: 4px;
              cursor: pointer;
              margin-right: 10px;
              font-weight: bold;
              border: 1px solid #ccc;
            }
            .btn-primary { background-color: #2563eb; color: #fff; border-color: #2563eb; }
            .btn-secondary { background-color: #f3f4f6; color: #4b5563; }
            @media print {
              .no-print { display: none; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="letterhead">
            <div class="gov-text">REPUBLIC OF THE PHILIPPINES</div>
            <div class="municipality">MUNICIPALITY OF CONCEPCION</div>
            <div class="province">Province of Romblon</div>
            <div class="office">OFFICE OF THE MUNICIPAL TREASURER</div>
            <div class="system-title">MUNICIPAL WATERWORKS SYSTEM</div>
          </div>
          <div class="divider-line"></div>
          
          <h2 class="report-title">${title}</h2>

          <div class="print-container">
            ${contentHtml}
          </div>
          <div class="no-print">
            <button class="btn btn-primary" onclick="window.print()">Print</button>
            <button class="btn btn-secondary" onclick="window.close()">Close</button>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleResetFilters = () => {
    setSelectedBarangays([]);
    setSelectedTypes([]);
    setSelectedStatus('all');
    if (availableMonths.length > 0) {
      setSelectedMonths([availableMonths[0]]);
    } else {
      setSelectedMonths([]);
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BarChartIcon sx={{ color: '#059669' }} /> Analytics & Municipal Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate official water usage, revenue realization, collection, aging accounts, and consumer demographic reports.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={loading || !isExportEnabled()}
            sx={{ borderRadius: 2.5, bgcolor: '#059669', color: '#ffffff', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#047857' } }}
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={loading || !isExportEnabled()}
            sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600, textTransform: 'none' }}
          >
            Print Report
          </Button>
          <Button 
            variant="outlined" 
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />} 
            onClick={loadReports}
            disabled={loading}
            sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600, textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2.5 }}>{error}</Alert>}

      {/* Modern Filter Card */}
      <Card sx={{ mb: 3, p: 2.5, borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Billing Months</InputLabel>
              <Select
                multiple
                value={selectedMonths}
                label="Billing Months"
                onChange={(e) => setSelectedMonths(e.target.value)}
                renderValue={(selected) => selected.map(formatMonth).join(', ')}
                sx={{ borderRadius: 2.5 }}
              >
                {availableMonths.map((m) => (
                  <MenuItem key={m} value={m}>
                    <Checkbox checked={selectedMonths.indexOf(m) > -1} sx={{ color: '#059669', '&.Mui-checked': { color: '#059669' } }} />
                    <ListItemText primary={formatMonth(m)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Barangay</InputLabel>
              <Select
                multiple
                value={selectedBarangays}
                label="Barangay"
                onChange={(e) => setSelectedBarangays(e.target.value)}
                renderValue={(selected) => selected.length === 0 ? "All Barangays" : selected.join(', ')}
                sx={{ borderRadius: 2.5 }}
              >
                {barangays.map(b => (
                  <MenuItem key={b} value={b}>
                    <Checkbox checked={selectedBarangays.indexOf(b) > -1} sx={{ color: '#059669', '&.Mui-checked': { color: '#059669' } }} />
                    <ListItemText primary={b} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Classification Type</InputLabel>
              <Select
                multiple
                value={selectedTypes}
                label="Classification Type"
                onChange={(e) => setSelectedTypes(e.target.value)}
                renderValue={(selected) => selected.length === 0 ? "All Types" : selected.join(', ')}
                sx={{ borderRadius: 2.5 }}
              >
                {customerTypes.map(t => (
                  <MenuItem key={t} value={t}>
                    <Checkbox checked={selectedTypes.indexOf(t) > -1} sx={{ color: '#059669', '&.Mui-checked': { color: '#059669' } }} />
                    <ListItemText primary={t} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={selectedStatus} label="Status" onChange={(e) => setSelectedStatus(e.target.value)} sx={{ borderRadius: 2.5 }}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
                <MenuItem value="Suspended">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" size="small" onClick={handleSelectAllMonths} sx={{ borderRadius: 2, borderColor: '#e2e8f0', color: '#475569', textTransform: 'none' }}>Select All Months</Button>
            <Button variant="outlined" size="small" onClick={handleClearAllMonths} sx={{ borderRadius: 2, borderColor: '#e2e8f0', color: '#475569', textTransform: 'none' }}>Clear All Months</Button>
            <Button variant="outlined" size="small" onClick={handleResetFilters} sx={{ borderRadius: 2, borderColor: '#fecaca', color: '#dc2626', textTransform: 'none' }}>Reset Filters</Button>
          </Grid>
        </Grid>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3 }}>
        <Tabs 
          value={activeReport} 
          onChange={(e, v) => setActiveReport(v)} 
          variant="scrollable" 
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#059669',
              height: 3,
              borderRadius: '3px 3px 0 0'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              minHeight: 48,
              '&.Mui-selected': {
                color: '#059669'
              }
            }
          }}
        >
          <Tab icon={<WaterIcon />} iconPosition="start" label="Water Consumption" value="water" />
          <Tab icon={<MoneyIcon />} iconPosition="start" label="Revenue & Billing" value="revenue" />
          <Tab icon={<ReceiptIcon />} iconPosition="start" label="Payment Collections" value="collections" />
          <Tab icon={<ClockIcon />} iconPosition="start" label="Accounts Receivable (Aging)" value="aging" />
          <Tab icon={<PeopleIcon />} iconPosition="start" label="Consumer Demographics" value="customers" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
          <CircularProgress sx={{ color: '#059669' }} size={32} />
          <Typography sx={{ ml: 2, color: '#64748b', fontWeight: 500 }}>Generating analytics report...</Typography>
        </Box>
      ) : (
        <Box>
          {/* Water Usage Report */}
          {activeReport === 'water' && waterReport && (
            <Stack spacing={3}>
              <Card sx={{ borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)', overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#0f172a">Water Consumption by Barangay</Typography>
                  <Stack direction="row" spacing={1.5}>
                    <Button 
                      variant="contained" 
                      size="small" 
                      startIcon={<DownloadIcon />} 
                      onClick={handleExportWaterUsageExcel}
                      sx={{ borderRadius: 2, bgcolor: '#059669', color: '#ffffff', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#047857' } }}
                    >
                      Export Sheet
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<PrintIcon />} 
                      onClick={handlePrint}
                      sx={{ borderRadius: 2, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600, textTransform: 'none' }}
                    >
                      Print
                    </Button>
                  </Stack>
                </Box>
                <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Barangay</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>No. of Customers</TableCell>
                            {selectedMonths.map(month => (
                              <TableCell key={month} sx={{ fontWeight: 'bold' }}>{formatMonth(month)}</TableCell>
                            ))}
                            {selectedMonths.length > 1 && (
                              <TableCell sx={{ fontWeight: 'bold' }}>Total Consumption</TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {waterReport.byBarangay.map((item) => (
                            <TableRow key={item.barangay} hover>
                              <TableCell sx={{ fontWeight: 'bold' }}>{formatBarangayName(item.barangay)}</TableCell>
                              <TableCell>{item.customerCount || 0}</TableCell>
                              {selectedMonths.map(month => (
                                <TableCell key={month}>
                                  {formatNumber(item.monthlyConsumption?.[month] || 0)} cu.m
                                </TableCell>
                              ))}
                              {selectedMonths.length > 1 && (
                                <TableCell sx={{ fontWeight: 'bold' }}>{formatNumber(item.consumption)} cu.m</TableCell>
                              )}
                            </TableRow>
                          ))}
                          {(() => {
                            const totals = {
                              monthly: {},
                              consumption: 0,
                              count: 0,
                              customerCount: 0
                            };
                            selectedMonths.forEach(m => { totals.monthly[m] = 0; });
                            waterReport.byBarangay.forEach(item => {
                              selectedMonths.forEach(month => {
                                totals.monthly[month] += (item.monthlyConsumption?.[month] || 0);
                              });
                              totals.consumption += item.consumption;
                              totals.count += item.count;
                              totals.customerCount += (item.customerCount || 0);
                            });

                            return (
                              <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>{totals.customerCount}</TableCell>
                                {selectedMonths.map(month => (
                                  <TableCell key={month} sx={{ fontWeight: 'bold' }}>
                                    {formatNumber(totals.monthly[month] || 0)} cu.m
                                  </TableCell>
                                ))}
                                {selectedMonths.length > 1 && (
                                  <TableCell sx={{ fontWeight: 'bold' }}>{formatNumber(totals.consumption)} cu.m</TableCell>
                                )}
                              </TableRow>
                            );
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                </Card>
              </Stack>
            )}

            {/* Revenue Report */}
            {activeReport === 'revenue' && revenueReport && (
              <Stack spacing={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: 'info.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="info.dark">Total Billed</Typography>
                        <Typography variant="h5" fontWeight="bold" color="info.main">{formatCurrency(revenueReport.totalBilled)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: 'success.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="success.dark">Total Paid</Typography>
                        <Typography variant="h5" fontWeight="bold" color="success.main">{formatCurrency(revenueReport.totalPaid)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: 'error.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="error.dark">Total Unpaid</Typography>
                        <Typography variant="h5" fontWeight="bold" color="error.main">{formatCurrency(revenueReport.totalUnpaid)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ bgcolor: 'primary.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="primary.dark">Collection Rate</Typography>
                        <Typography variant="h5" fontWeight="bold" color="primary.main">{revenueReport.collectionRate.toFixed(1)}%</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">Monthly Revenue</Typography>
                      <Button startIcon={<DownloadIcon />} size="small" onClick={() => exportToCSV(revenueReport.byMonth, 'monthly_revenue')}>Export</Button>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Month</TableCell>
                            <TableCell>Billed</TableCell>
                            <TableCell>Paid</TableCell>
                            <TableCell>Unpaid</TableCell>
                            <TableCell>Collection %</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {revenueReport.byMonth.map((item) => (
                            <TableRow key={item.month} hover>
                              <TableCell>{formatMonth(item.month)}</TableCell>
                              <TableCell>{formatCurrency(item.billed)}</TableCell>
                              <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>{formatCurrency(item.paid)}</TableCell>
                              <TableCell sx={{ color: 'error.main', fontWeight: 'bold' }}>{formatCurrency(item.unpaid)}</TableCell>
                              <TableCell>{item.billed > 0 ? ((item.paid / item.billed) * 100).toFixed(1) : 0}%</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Stack>
            )}

            {/* Collections Report */}
            {activeReport === 'collections' && collectionReport && (
              <Stack spacing={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'success.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="success.dark">Total Collected</Typography>
                        <Typography variant="h4" fontWeight="bold" color="success.main">{formatCurrency(collectionReport.totalCollected)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'info.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="info.dark">Collection Count</Typography>
                        <Typography variant="h4" fontWeight="bold" color="info.main">{collectionReport.collectionCount.toLocaleString()}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'primary.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="primary.dark">Average per Collection</Typography>
                        <Typography variant="h4" fontWeight="bold" color="primary.main">
                          {formatCurrency(collectionReport.totalCollected / collectionReport.collectionCount)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">Daily Collections</Typography>
                      <Button startIcon={<DownloadIcon />} size="small" onClick={() => exportToCSV(collectionReport.byDate, 'daily_collections')}>Export</Button>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Amount Collected</TableCell>
                            <TableCell>Count</TableCell>
                            <TableCell>Average</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {collectionReport.byDate.map((item) => (
                            <TableRow key={item.date} hover>
                              <TableCell>{new Date(item.date).toLocaleDateString('en-PH')}</TableCell>
                              <TableCell sx={{ color: 'success.main', fontWeight: 'bold' }}>{formatCurrency(item.amount)}</TableCell>
                              <TableCell>{item.count.toLocaleString()}</TableCell>
                              <TableCell>{formatCurrency(item.amount / item.count)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Stack>
            )}

            {/* Aging Report */}
            {activeReport === 'aging' && agingReport && (
              <Stack spacing={3}>
                <Grid container spacing={2}>
                  {Object.entries(agingReport.summary).map(([key, value]) => {
                    let title = key;
                    let color = 'info';
                    if (key === 'current') { title = 'Current (0-30)'; color = 'success'; }
                    if (key === 'days_31_60') { title = '31-60 Days'; color = 'warning'; }
                    if (key === 'days_61_90') { title = '61-90 Days'; color = 'warning'; }
                    if (key === 'days_91_120') { title = '91-120 Days'; color = 'error'; }
                    if (key === 'over_120') { title = 'Over 120 Days'; color = 'error'; }

                    return (
                      <Grid item xs={12} sm={6} md={2.4} key={key}>
                        <Card sx={{ bgcolor: `${color}.lighter`, height: '100%' }}>
                          <CardContent>
                            <Typography variant="subtitle2" color={`${color}.dark`}>{title}</Typography>
                            <Typography variant="h6" fontWeight="bold" color={`${color}.main`}>{formatCurrency(value.amount)}</Typography>
                            <Typography variant="caption">{value.count} bills</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Aging Distribution</Typography>
                    <Box sx={{ display: 'flex', height: 40, borderRadius: 1, overflow: 'hidden', mt: 2 }}>
                      {Object.entries(agingReport.summary).map(([key, value], index) => {
                        const percentage = agingReport.totalAmount > 0 ? (value.amount / agingReport.totalAmount) * 100 : 0;
                        const colors = ['#4caf50', '#ff9800', '#ff5722', '#f44336', '#b71c1c']; // Colors for buckets
                        return percentage > 0 ? (
                          <Box
                            key={key}
                            sx={{
                              width: `${percentage}%`,
                              bgcolor: colors[index],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}
                            title={`${key}: ${formatCurrency(value.amount)} (${percentage.toFixed(1)}%)`}
                          >
                            {percentage > 10 ? `${percentage.toFixed(0)}%` : ''}
                          </Box>
                        ) : null;
                      })}
                    </Box>
                  </CardContent>
                </Card>
              </Stack>
            )}

            {/* Customer Analysis */}
            {activeReport === 'customers' && customerReport && (
              <Stack spacing={3}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'info.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="info.dark">Total Customers</Typography>
                        <Typography variant="h4" fontWeight="bold" color="info.main">{customerReport.total.toLocaleString()}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'success.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="success.dark">Active Customers</Typography>
                        <Typography variant="h4" fontWeight="bold" color="success.main">{customerReport.active.toLocaleString()}</Typography>
                        <Typography variant="caption">{((customerReport.active / customerReport.total) * 100).toFixed(1)}% of total</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'warning.lighter' }}>
                      <CardContent>
                        <Typography variant="subtitle2" color="warning.dark">Inactive Customers</Typography>
                        <Typography variant="h4" fontWeight="bold" color="warning.main">{customerReport.inactive.toLocaleString()}</Typography>
                        <Typography variant="caption">{((customerReport.inactive / customerReport.total) * 100).toFixed(1)}% of total</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Customers by Status</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow><TableCell>Status</TableCell><TableCell>Count</TableCell><TableCell>Percentage</TableCell></TableRow>
                            </TableHead>
                            <TableBody>
                              {customerReport.byStatus.map((item) => (
                                <TableRow key={item.status}>
                                  <TableCell fontWeight="bold">{item.status}</TableCell>
                                  <TableCell>{item.count.toLocaleString()}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LinearProgress variant="determinate" value={(item.count / customerReport.total) * 100} sx={{ flexGrow: 1 }} />
                                      <Typography variant="caption">{((item.count / customerReport.total) * 100).toFixed(1)}%</Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Customers by Type</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow><TableCell>Type</TableCell><TableCell>Count</TableCell><TableCell>Percentage</TableCell></TableRow>
                            </TableHead>
                            <TableBody>
                              {customerReport.byType.map((item) => (
                                <TableRow key={item.type}>
                                  <TableCell fontWeight="bold">{item.type}</TableCell>
                                  <TableCell>{item.count.toLocaleString()}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <LinearProgress variant="determinate" value={(item.count / customerReport.total) * 100} sx={{ flexGrow: 1 }} color="info" />
                                      <Typography variant="caption">{((item.count / customerReport.total) * 100).toFixed(1)}%</Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Stack>
            )}
          </Box>
        )}
      </Box>
  );
};

export default ReportsPage;
